import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import PublicNav from './PublicNav';
import { Motif } from './ui/Motif';
import { Reveal, RevealGroup } from './ui/Reveal';
import MarketingFooter from './MarketingFooter';

const BG = '#F5FAFC';
const INK = '#002A38';
const MUTED = '#78716C';
const TERRA = '#0076A3';
const LINE = '#ECE7DE';

const ACCENTS = [
  { tile: '#FFE7D9', ink: '#0076A3' },
  { tile: '#E6F0E7', ink: '#5B8C63' },
  { tile: '#FDEFD7', ink: '#D08A2C' },
  { tile: '#F1E4F5', ink: '#9A5BB0' },
];

const overline = { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: TERRA, marginBottom: '0.75rem' };

export default function ProductPageTemplate({ data }) {
  const navigate = useNavigate();
  const goCta = (to) => {
    if (typeof to === 'string' && /^https?:\/\//.test(to)) {
      window.open(to, '_blank', 'noopener,noreferrer');
    } else {
      navigate(to);
    }
  };
  const { hero, sections, cta } = data;

  return (
    <div style={{ minHeight: '100vh', background: BG, color: INK, overflowX: 'hidden' }}>
      <PublicNav />

      {/* ─── Hero ─── */}
      <section style={{ position: 'relative', padding: 'clamp(3rem, 6vw, 6rem) clamp(1.25rem, 5vw, 5rem)', maxWidth: 1240, margin: '0 auto' }}>
        <div className="grain-overlay" style={{ opacity: 0.16 }} />
        <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: '14%', right: '6%', width: 44, color: TERRA, opacity: 0.8 }} />
        <Motif shape="blob" className="motif-float" style={{ position: 'absolute', bottom: '4%', right: '24%', width: 64, color: '#8FD0E8', opacity: 0.32, animationDelay: '1.2s' }} />
        <Motif shape="ring" className="motif-float" stroke style={{ position: 'absolute', top: '20%', left: '2%', width: 58, color: '#A9DCF2', opacity: 0.5, animationDelay: '0.6s' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 760 }}>
          <Reveal now>
            <div data-testid="product-hero-label" style={overline}>{hero.label}</div>
          </Reveal>
          <Reveal now delay={0.06}>
            <h1 data-testid="product-hero-title" style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(2.25rem, 5vw, 3.75rem)', fontWeight: 800, lineHeight: 1.04, letterSpacing: '-0.03em', marginBottom: '1.25rem' }}>{hero.title}</h1>
          </Reveal>
          <Reveal now delay={0.14}>
            <p style={{ fontSize: 'clamp(1.05rem, 1.5vw, 1.2rem)', color: MUTED, lineHeight: 1.65, maxWidth: 620, marginBottom: '2rem' }}>{hero.subtitle}</p>
          </Reveal>
          <Reveal now delay={0.22}>
            <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap' }}>
              {hero.ctas ? hero.ctas.map((b, i) => (
                <Button key={b.testid || b.label || i} size="pill" variant={b.primary ? undefined : 'outline'} onClick={() => goCta(b.to)} data-testid={b.testid || `product-hero-cta-${i}`}>{b.label}</Button>
              )) : (
                <>
                  <Button size="pill" onClick={() => navigate('/login?mode=register&redirect=payment&plan=starter_monthly')} data-testid="product-hero-cta">Subscribe to Starter</Button>
                  <Button size="pill" variant="outline" onClick={() => navigate('/pricing')} data-testid="product-hero-pricing">View Pricing</Button>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Content Sections ─── */}
      {sections.map((section, idx) => {
        const white = idx % 2 === 1;
        const sectionBg = white ? '#FFFFFF' : BG;
        return (
          <section key={`section-${idx}`} style={{ background: sectionBg, borderTop: white ? `1px solid ${LINE}` : 'none', borderBottom: white ? `1px solid ${LINE}` : 'none' }}>
            <div style={{ maxWidth: 1160, margin: '0 auto', padding: 'clamp(3rem, 6vw, 5.5rem) clamp(1.25rem, 4vw, 3rem)' }}>
              {section.features ? (
                <>
                  <Reveal>
                    <div style={{ maxWidth: 660, marginBottom: '2.75rem' }}>
                      {section.label && <div style={overline}>{section.label}</div>}
                      <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(1.8rem, 4vw, 2.75rem)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.06, marginBottom: '0.75rem' }}>{section.title}</h2>
                      {section.subtitle && <p style={{ fontSize: '1.0625rem', color: MUTED, lineHeight: 1.6 }}>{section.subtitle}</p>}
                    </div>
                  </Reveal>
                  <RevealGroup stagger={0.06} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
                    {section.features.map((f, fi) => {
                      const a = ACCENTS[fi % ACCENTS.length];
                      return (
                        <Reveal key={`feat-${idx}-${fi}`}>
                          <div
                            style={{ background: white ? BG : '#fff', border: `1px solid ${LINE}`, borderRadius: 22, padding: '1.75rem', height: '100%', transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 24px 48px -28px rgba(28,25,23,0.35)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                            <div style={{ width: 52, height: 52, borderRadius: 15, background: a.tile, color: a.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.1rem', fontSize: '1.5rem' }}>{f.icon}</div>
                            <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem' }}>{f.title}</h4>
                            <p style={{ fontSize: '0.875rem', color: MUTED, lineHeight: 1.65 }}>{f.desc}</p>
                          </div>
                        </Reveal>
                      );
                    })}
                  </RevealGroup>
                </>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'clamp(2rem, 4vw, 4rem)', alignItems: 'center' }}>
                  <Reveal style={{ order: idx % 2 === 0 ? 0 : 1 }}>
                    <div>
                      {section.label && <div style={overline}>{section.label}</div>}
                      <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(1.7rem, 3.5vw, 2.5rem)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '1rem' }}>{section.title}</h2>
                      <p style={{ fontSize: '1rem', color: MUTED, lineHeight: 1.7, marginBottom: section.bullets ? '1.5rem' : 0 }}>{section.desc}</p>
                      {section.bullets && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                          {section.bullets.map((b, bi) => (
                            <li key={`b-${idx}-${bi}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem', fontSize: '0.9375rem', color: '#44403C' }}>
                              <span style={{ flexShrink: 0, marginTop: 1, width: 22, height: 22, borderRadius: 999, background: '#FFE7D9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TERRA} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                              </span>
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Reveal>
                  <Reveal delay={0.1} style={{ order: idx % 2 === 0 ? 1 : 0 }}>
                    {section.visualNode ? section.visualNode : (() => {
                      const a = ACCENTS[idx % ACCENTS.length];
                      return (
                        <div style={{ position: 'relative', borderRadius: 28, minHeight: 300, background: `linear-gradient(150deg, ${a.tile} 0%, #FFFFFF 100%)`, border: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          <Motif shape="petal" className="motif-float" style={{ position: 'absolute', top: -14, right: -10, width: 56, color: a.ink, opacity: 0.35 }} />
                          <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                            <div style={{ fontSize: '3.5rem', marginBottom: '0.5rem' }}>{section.visual || '📊'}</div>
                            <p style={{ fontSize: '0.85rem', color: MUTED, fontWeight: 600 }}>{section.visualLabel || ''}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </Reveal>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* ─── Bottom CTA ─── */}
      <section style={{ padding: 'clamp(3rem, 6vw, 6rem) clamp(1.25rem, 4vw, 3rem)', maxWidth: 1100, margin: '0 auto' }}>
        <Reveal>
          <div style={{ position: 'relative', overflow: 'hidden', background: INK, borderRadius: 32, padding: 'clamp(2.5rem, 6vw, 4.5rem)', textAlign: 'center', color: '#fff' }}>
            <div className="grain-overlay" style={{ opacity: 0.25 }} />
            <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: 28, left: '8%', width: 40, color: TERRA, opacity: 0.9 }} />
            <Motif shape="blob" className="motif-float" style={{ position: 'absolute', bottom: 20, right: '8%', width: 64, color: '#A9DCF2', opacity: 0.5, animationDelay: '1s' }} />
            <div style={{ position: 'relative', zIndex: 1, maxWidth: 620, margin: '0 auto' }}>
              <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(2rem, 4.5vw, 3rem)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.08, marginBottom: '1rem' }}>{cta?.title || 'Ready to get started?'}</h2>
              <p style={{ fontSize: '1.125rem', color: 'rgba(255,255,255,0.7)', marginBottom: '2rem', lineHeight: 1.6 }}>{cta?.desc || 'Get started with CHIRE today.'}</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
                {cta?.ctas ? cta.ctas.map((b, i) => (
                  <Button key={b.testid || b.label || i} size="pill" variant={b.primary ? undefined : 'outline'} onClick={() => goCta(b.to)} data-testid={b.testid || `product-cta-${i}`}
                    style={b.primary ? { background: TERRA, color: '#fff', border: 'none' } : { background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>{b.label}</Button>
                )) : (
                  <>
                    <Button size="pill" onClick={() => navigate('/login?mode=register&redirect=payment&plan=starter_monthly')} data-testid="product-cta-start" style={{ background: TERRA, color: '#fff', border: 'none' }}>Subscribe to Starter</Button>
                    <Button size="pill" variant="outline" onClick={() => navigate('/pricing')} data-testid="product-cta-pricing" style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>View Pricing</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <MarketingFooter />
    </div>
  );
}
